import {
  Button,
  ButtonVariants,
  Flex,
  FormControlSizes,
  IconButton,
  IconName,
  MixpanelEvent,
  MixpanelEventSource,
  Tooltip,
  useMixpanel,
} from '@talos/kyoko';
import { useCallback } from 'react';
import type { LayoutSection } from '../../../types/LayoutConfig';

interface ManageSpaceButtonsProps {
  layoutSection: LayoutSection;
  isRearranging: boolean;
  onSaveRearrange: () => void;
  onCancelRearrange: () => void;
  onOpenAddSpace: () => void;
  onOpenCreateTemplate: () => void;
}

export function ManageSpaceButtons({
  layoutSection,
  isRearranging,
  onSaveRearrange,
  onCancelRearrange,
  onOpenAddSpace,
  onOpenCreateTemplate,
}: ManageSpaceButtonsProps) {
  const mixpanel = useMixpanel();

  const handleAddSpace = useCallback(() => {
    onOpenAddSpace();
    mixpanel.track(MixpanelEvent.OpenAddNewSpaceModal);
  }, [onOpenAddSpace, mixpanel]);

  const handleOpenCreateTemplate = useCallback(() => {
    onOpenCreateTemplate();
    mixpanel.track(MixpanelEvent.OpenCreateTemplateModal, {
      section: layoutSection,
      source: MixpanelEventSource.NavigationSidebar,
    });
  }, [onOpenCreateTemplate, mixpanel, layoutSection]);

  if (isRearranging) {
    return (
      <Flex gap="spacing8">
        <Button
          data-testid="rearrange-save-button"
          size={FormControlSizes.Small}
          variant={ButtonVariants.Positive}
          onClick={onSaveRearrange}
          width="100%"
        >
          Save
        </Button>
        <Tooltip tooltip="Cancel" closeOnClickOutside={true}>
          <Button data-testid="rearrange-cancel-button" size={FormControlSizes.Small} onClick={onCancelRearrange}>
            Cancel
          </Button>
        </Tooltip>
      </Flex>
    );
  }

  return (
    <Flex gap="spacing8">
      <Button
        size={FormControlSizes.Small}
        onClick={handleAddSpace}
        dim
        startIcon={IconName.Plus}
        data-testid="new-space-button"
        width="100%"
      >
        New Space
      </Button>
      <Tooltip tooltip="Create a new template" closeOnClickOutside={true}>
        <IconButton
          data-testid="create-template-button"
          dim
          icon={IconName.Template}
          size={FormControlSizes.Small}
          onClick={handleOpenCreateTemplate}
        />
      </Tooltip>
    </Flex>
  );
}
